import { isEqual, isEqualWith } from 'lodash';
import {
  ExteriorNodeLabelModel,
  ExteriorNodeLabelModelPosition,
  FreeEdgeLabelModel,
  FreeNodeLabelModel,
  IEdge,
  IGraph,
  ILabel,
  ILabelModelParameter,
  ILabelOwner,
  IModelItem,
  INode,
  INodeStyle,
  Insets,
  InteriorNodeLabelModel,
  InteriorNodeLabelModelPosition,
  Point,
  Rect,
  Size,
  SmartEdgeLabelModel,
} from '@ardoq/yfiles';
import {
  AggregatedGraphEdge,
  CollapsibleGraphGroup,
  GraphItem,
  GraphNode,
  type GraphEdgeBase,
  isAggregatedGraphEdge,
  ComponentGraphAggregatedEdgeModel,
} from '@ardoq/graph';
import { AggregatedEdgeLabelStyle } from '@ardoq/graph/yfiles';
import * as encodingUtils from '@ardoq/html';
import { logError } from '@ardoq/logging';
import { DiffType, type LoadedScenarioData } from '@ardoq/data-model';
import { componentUtils } from '@ardoq/scope-data';
import { toIdSet } from '@ardoq/core';
import { ViewIds } from '@ardoq/api-types';
import { AggregateEdgeStyle } from './AggregateEdgeStyle';
import { CollapsibleGroupStyle } from './CollapsibleGroupStyle';
import { ArdoqURLLabelStyle } from 'yfilesExtensions/styles/ardoqURLLabelStyle';
import { getDefaultSize } from 'yfilesExtensions/styles/styleLoader';
import { groupBounds } from './GroupBoundsCalculator';
import { ArdoqAddToScenario } from 'yfilesExtensions/styles/ArdoqAddToScenario';
import { isContextNode } from 'yfilesExtensions/styles/nodeDecorator';
import { getNodeLayoutSize } from 'yfilesExtensions/getNodeLayoutSize';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  CONTEXT_HIGHLIGHT_STROKE,
  NODE_HEIGHT,
  NODE_WIDTH,
} from 'yfilesExtensions/styles/consts';
import { urlNodeButtonModelParameter } from './labelParameters';
import { getCssClassFromDiffType } from '@ardoq/common-helpers';
import { sizeEmptyGroupNodes } from './sizeEmptyGroupNodes';
import { MultiLabelStyle } from './labels/MultiLabelStyle';
import { MultiLabelGroupStyle } from './labels/MultiLabelGroupStyle';
import { updateEdgeLabels } from './modernized/graphBuilder/updateGraphLabels';
import { WorkspaceModeOpenGroupUrlLabelStyle as WorkspaceModelOpenGroupUrlLabelStyle } from 'yfilesExtensions/styles/WorkspaceModeOpenGroupUrlLabelStyle';
import { EXPANDER_RADIUS } from '../consts';
import { diffUpdateGraph } from 'yfilesExtensions/utils/diffUpdateGraph';

type GraphViewData = {
  nodes: GraphNode[];
  groups: CollapsibleGraphGroup[];
  edges: GraphEdgeBase[];
};

const groupNodeSize = (groupNode: INode) => groupBounds(groupNode).toSize();

const seedLocation = (ancestor: INode, seedIndex: number): Point =>
  ancestor.layout.center.subtract(new Point(seedIndex, seedIndex));

const getInitialLayout = (ancestor: INode | null, seedIndex: number) =>
  // if this is a descendant of a node which has been expanded, its origin (for animation purposes) should be the group center.
  ancestor
    ? new Rect(seedLocation(ancestor, seedIndex), ancestor.layout.toSize())
    : null;

const getInitialGroupLayout = (ancestor: INode | null, seedIndex: number) =>
  ancestor ? new Rect(seedLocation(ancestor, seedIndex), new Size(1, 1)) : null;

const isCollapsedComponentGroup = (group: CollapsibleGraphGroup) =>
  group.collapsed && group.isComponent();

const graphItemsEqual = (
  a: GraphItem | GraphItem[],
  b: GraphItem | GraphItem
) => !Array.isArray(a) && a.isEqual(b);

const isURLLabel = (label: ILabel): label is ILabel & { tag: GraphItem } => {
  return label.style instanceof ArdoqURLLabelStyle;
};

const updateLabels = (
  owner: ILabelOwner,
  graph: IGraph,
  graphItem: GraphItem
) => {
  const labelsToRemove: ILabel[] = [];
  owner.labels.forEach(label => {
    if (label.tag === null) {
      graph.setLabelText(label, graphItem.getItemLabels()?.mainLabel ?? '');
    } else if (isURLLabel(label) && !label.tag.hasURLFields()) {
      labelsToRemove.push(label);
    }
  });
  labelsToRemove.forEach(label => graph.remove(label));
};
const urlButtonStyle = ArdoqURLLabelStyle.Instance;

/** The label parameter specifying the location of URL icons in uncollapsed groups */
const urlGroupNodeButtonModelParameter = new InteriorNodeLabelModel({
  padding: new Insets(EXPANDER_RADIUS, EXPANDER_RADIUS, 0, 0),
}).createParameter(InteriorNodeLabelModelPosition.TOP_RIGHT);

const urlEdgeButtonModelParameter =
  FreeEdgeLabelModel.INSTANCE.createParameter();

const addToScenarioButtonStyle = new ArdoqAddToScenario();
const addToScenarioNodeButtonModelParameter =
  FreeNodeLabelModel.INSTANCE.createParameter(
    new Point(0.5, 0), // place label at node layout top-center
    new Point(0, -5), // move up 5px
    new Point(0.5, 1) // anchor point of label is bottom-center
  );

const aggregatedEdgeCountLabelStyle = new AggregatedEdgeLabelStyle(
  getCssClassFromDiffType
);
const aggregatedEdgeCountLabelParameter =
  new SmartEdgeLabelModel().createParameterFromCenter();

const southsideLabelParameter = new ExteriorNodeLabelModel({
  margins: CONTEXT_HIGHLIGHT_STROKE,
}).createParameter(ExteriorNodeLabelModelPosition.BOTTOM);

const groupLabelLayoutParameter = InteriorNodeLabelModel.TOP_LEFT;

export class CollapsibleGroupsYGraphBuilder {
  private edgeMap = new Map<string, IEdge>();
  private nodeMap = new Map<string, INode>();
  private groupNodeMap = new Map<string, INode>();
  nodesSource: GraphNode[] = [];
  edgesSource: GraphEdgeBase[] = [];
  groupsSource: CollapsibleGraphGroup[] = [];
  public loadedScenarioData: LoadedScenarioData | null = null;
  private scenarioComponentIds: Set<string> = new Set();
  private connectedScenarioComponentIds: Set<string> = new Set();
  constructor(
    viewId: ViewIds,
    viewInstanceId: string,
    private graph: IGraph,
    private getNodeStyle: (node: GraphNode) => INodeStyle,
    private getNodeSize: (nodeStyle: INodeStyle) => Size
  ) {
    // Initialization
    graph.edgeDefaults.style = new AggregateEdgeStyle();

    graph.groupNodeDefaults.style = new CollapsibleGroupStyle(
      viewId,
      viewInstanceId
    );

    graph.groupNodeDefaults.labels.style = MultiLabelGroupStyle.Instance;
    graph.groupNodeDefaults.labels.layoutParameter = groupLabelLayoutParameter;
    // Style and labels
    graph.nodeDefaults.labels.layoutParameter = southsideLabelParameter;
    const labelStyle = MultiLabelStyle.Instance;
    graph.edgeDefaults.labels.style = labelStyle;
    graph.nodeDefaults.labels.style = labelStyle;

    const edgeLabelModel = new FreeEdgeLabelModel();
    graph.edgeDefaults.labels.layoutParameter =
      edgeLabelModel.createParameter();
  }

  public updateScenarioData(loadedScenarioData: LoadedScenarioData | null) {
    this.loadedScenarioData = loadedScenarioData;
    if (loadedScenarioData?.scenarioData) {
      const { components, connected } = loadedScenarioData.scenarioData;
      this.scenarioComponentIds = toIdSet(components);
      this.connectedScenarioComponentIds = toIdSet(connected.components);
    } else {
      this.scenarioComponentIds.clear();
      this.connectedScenarioComponentIds.clear();
    }
  }

  private isScenarioRelated(componentId: string): boolean {
    return componentUtils.isComponentScenarioRelated(
      componentId,
      this.scenarioComponentIds,
      this.connectedScenarioComponentIds
    );
  }

  private createEdge = (graphEdge: GraphEdgeBase) => {
    const sourceNode =
      this.nodeMap.get(graphEdge.source) ||
      this.groupNodeMap.get(graphEdge.source);
    const targetNode =
      this.nodeMap.get(graphEdge.target) ||
      this.groupNodeMap.get(graphEdge.target);
    if (!sourceNode || !targetNode) {
      logError(Error('Source or Target node not found while creating Edge.'));
      return null;
    }

    const edge = this.graph.createEdge({
      tag: graphEdge,
      labels: [
        encodingUtils.unescapeHTML(graphEdge.getItemLabels()?.mainLabel ?? ''),
      ],
      source: sourceNode,
      target: targetNode,
    });

    if (graphEdge.hasURLFields()) {
      this.addURLEdgeLabel(edge, graphEdge);
    }
    if (isAggregatedGraphEdge(graphEdge)) {
      this.addAggregatedCountLabel(edge, graphEdge);
    }
    return edge;
  };
  private updateEdge = (graphEdge: GraphEdgeBase) => {
    const edge = this.edgeMap.get(graphEdge.id);
    if (!edge) {
      logError(Error('Edge not found in updateEdge.'));
      return null;
    }

    if (
      graphEdge instanceof GraphItem &&
      !isEqual(edge.tag.getItemLabels(), graphEdge.getItemLabels())
    ) {
      this.removeEdge(edge);
      return this.createEdge(graphEdge);
    }

    // Create new edge if sourcePort/targetPort is gone (group Node turned into a normal node or vice-versa)
    if (!this.graph.contains(edge) || !edge.sourcePort || !edge.targetPort) {
      return this.createEdge(graphEdge);
    }
    if (
      graphEdge.hasURLFields() &&
      !edge.labels.find(label => isURLLabel(label))
    ) {
      this.addURLEdgeLabel(edge, graphEdge);
    }
    if (
      edge.sourceNode?.tag instanceof GraphItem &&
      edge.targetNode?.tag instanceof GraphItem
    ) {
      if (
        edge.sourceNode.tag.id !== graphEdge.source ||
        edge.targetNode.tag.id !== graphEdge.target
      ) {
        // if source or target has changed, we must create a new edge.
        this.removeEdge(edge);
        return this.createEdge(graphEdge);
      }
    } else {
      logError(Error('Unexpected edge source or target.'));
    }
    updateEdgeLabels(edge, this.graph, graphEdge, false);
    edge.tag = graphEdge; // could be a new GraphEdge with the same id.
    return edge;
  };

  private seedIndex = 0;
  private nextSeedIndex = (): number => this.seedIndex++;

  private getAncestorInGraph = (graphNode: GraphItem): INode | null =>
    (graphNode.parent &&
      (this.groupNodeMap.get(graphNode.parent.id) ??
        this.getAncestorInGraph(graphNode.parent))) ??
    null;

  private createNode = (graphNode: GraphNode) => {
    const node = this.graph.createNode({
      parent: graphNode.parent
        ? (this.groupNodeMap.get(graphNode.parent.id) ?? null)
        : null,
      tag: graphNode,
      labels: [graphNode.getItemLabels()?.mainLabel ?? ''],
      layout:
        getInitialLayout(
          this.getAncestorInGraph(graphNode),
          this.nextSeedIndex()
        ) ?? undefined,
    });

    if (graphNode.hasURLFields()) {
      this.addURLNodeLabel(node, graphNode);
    }
    if (
      !(graphNode.getVisualDiffType() === DiffType.PLACEHOLDER) &&
      this.isScenarioRelated(graphNode.modelId)
    ) {
      this.addToScenarioButton(node, graphNode);
    }
    this.updateNodeLayout(node);
    return node;
  };
  private updateNode = (graphNode: GraphNode) => {
    const node = this.nodeMap.get(graphNode.id)!;
    if (!node) {
      logError(Error('Node not found in updateNode.'));
      return null;
    }

    if (!isEqual(node.tag.getItemLabels?.(), graphNode.getItemLabels())) {
      this.removeItem(node, this.nodeMap);
      return this.createNode(graphNode);
    }

    const style = this.getNodeStyle(graphNode);

    if (style !== node.style) {
      this.graph.setStyle(node, style);
    }
    this.updateNodeLayout(node);

    if (
      graphNode.hasURLFields() &&
      !node.labels.find(label => isURLLabel(label))
    ) {
      this.addURLNodeLabel(node, graphNode);
    }

    updateLabels(node, this.graph, graphNode);

    if (graphNode.parent) {
      const parentNode = this.groupNodeMap.get(graphNode.parent.id);
      if (parentNode) {
        this.graph.setParent(node, parentNode);
      } else {
        logError(Error('Parent node not found.'));
      }
    } else {
      this.graph.setParent(node, null);
    }
    if (
      !(graphNode.getVisualDiffType() === DiffType.PLACEHOLDER) &&
      this.isScenarioRelated(graphNode.modelId)
    ) {
      this.addToScenarioButton(node, graphNode);
    }
    return node;
  };

  private addGroupNodeLabels = (
    graphNode: CollapsibleGraphGroup,
    groupNode: INode
  ) => {
    const text = graphNode.getItemLabels()?.mainLabel ?? '';

    const label = this.graph.addLabel({
      owner: groupNode,
      text,
    });
    this.graph.setStyle(
      label,
      isCollapsedComponentGroup(graphNode)
        ? MultiLabelStyle.Instance
        : MultiLabelGroupStyle.Instance
    );
    this.graph.setLabelLayoutParameter(
      label,
      isCollapsedComponentGroup(graphNode)
        ? southsideLabelParameter
        : groupLabelLayoutParameter
    );
    if (graphNode.hasURLFields()) {
      this.addGroupURLNodeLabel(groupNode, graphNode);
    }
  };
  private createGroupNode = (graphNode: CollapsibleGraphGroup) => {
    const groupNode = this.graph.createGroupNode({
      tag: graphNode,
      layout:
        getInitialGroupLayout(
          this.getAncestorInGraph(graphNode),
          this.nextSeedIndex()
        ) ?? undefined,
    });
    this.addGroupNodeLabels(graphNode, groupNode);
    return groupNode;
  };
  private updateGroupNode = (graphNode: CollapsibleGraphGroup) => {
    const groupNode = this.groupNodeMap.get(graphNode.id);
    if (!groupNode) {
      logError(Error('Group node not found.'));
      return null;
    }
    this.graph.setNodeLayout(
      groupNode,
      new Rect(groupNode.layout.center, new Size(1, 1))
    );
    groupNode.tag = graphNode; // this could be a new GraphGroup with the same id.
    groupNode.labels.toArray().forEach(label => this.graph.remove(label));

    this.addGroupNodeLabels(graphNode, groupNode);
    return groupNode;
  };

  private removeItem = (item: IModelItem, map: Map<string, IModelItem>) => {
    const businessObject: GraphItem = item.tag;
    map.delete(businessObject.id);
    if (this.graph.contains(item)) {
      this.graph.remove(item);
    }
  };
  private removeEdge = (edge: IEdge) => {
    // Make sure the edge is attached to the graph
    if (this.graph.contains(edge) && (edge.sourcePort || edge.targetPort)) {
      this.removeItem(edge, this.edgeMap);
    }
  };

  private addAggregatedCountLabel = (
    owner: IEdge,
    graphEdge: AggregatedGraphEdge | ComponentGraphAggregatedEdgeModel
  ) => {
    this.graph.addLabel({
      owner,
      text: `${graphEdge.modelIds.length}`,
      style: aggregatedEdgeCountLabelStyle,
      layoutParameter: aggregatedEdgeCountLabelParameter,
      tag: graphEdge,
    });
  };
  hasLayoutUpdate({ nodes, groups, edges }: GraphViewData) {
    const nodesUnchanged =
      !nodes || isEqualWith(nodes, this.nodesSource, graphItemsEqual);
    const groupsUnchanged =
      !groups || isEqualWith(groups, this.groupsSource, graphItemsEqual);
    const edgesUnchanged =
      !edges || isEqualWith(edges, this.edgesSource, graphItemsEqual);

    return !(nodesUnchanged && groupsUnchanged && edgesUnchanged);
  }
  clearEdges() {
    Array.from(this.edgeMap.values()).forEach(this.removeEdge);
  }
  clear() {
    this.graph.clear();

    this.groupNodeMap.clear();
    this.nodeMap.clear();
    this.edgeMap.clear();
  }
  buildGraph() {
    this.clear();

    this.groupNodeMap = new Map(
      this.groupsSource.map(graphNode => [
        graphNode.id,
        this.createGroupNode(graphNode),
      ])
    );

    this.setGroupNodeParents();

    this.nodeMap = new Map(
      this.nodesSource.map(graphNode => [
        graphNode.id,
        this.createNode(graphNode),
      ])
    );

    sizeEmptyGroupNodes(this.graph, groupNodeSize);

    this.edgeMap = new Map(
      this.edgesSource.map(graphEdge => {
        return [graphEdge.id, this.createEdge(graphEdge)!];
      })
    );
  }

  updateGraph() {
    this.seedIndex = 0;
    if (this.nodeMap.size + this.edgeMap.size + this.groupNodeMap.size === 0) {
      this.buildGraph();
      return;
    }

    this.groupNodeMap = diffUpdateGraph({
      oldMap: this.groupNodeMap,
      businessObjects: this.groupsSource,
      create: this.createGroupNode,
      update: this.updateGroupNode,
      remove: this.removeItem,
    });

    this.setGroupNodeParents();

    this.nodeMap = diffUpdateGraph<GraphNode, INode>({
      oldMap: this.nodeMap,
      businessObjects: this.nodesSource,
      create: this.createNode,
      update: this.updateNode,
      remove: this.removeItem,
    });

    sizeEmptyGroupNodes(this.graph, groupNodeSize);

    this.edgeMap = diffUpdateGraph({
      oldMap: this.edgeMap,
      businessObjects: this.edgesSource,
      create: this.createEdge,
      update: this.updateEdge,
      remove: this.removeEdge,
    });
  }
  private addURLLabel(
    owner: ILabelOwner,
    graphNode: GraphItem,
    buttonModelParameter: ILabelModelParameter
  ) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter: buttonModelParameter,
      style: urlButtonStyle,
      tag: graphNode,
    });
  }
  private addURLEdgeLabel(owner: IEdge, graphEdge: GraphEdgeBase) {
    this.addURLLabel(owner, graphEdge, urlEdgeButtonModelParameter);
  }
  private addURLNodeLabel(owner: INode, graphNode: GraphNode) {
    this.addURLLabel(owner, graphNode, urlNodeButtonModelParameter);
  }

  private addGroupURLNodeLabel(owner: INode, graphNode: CollapsibleGraphGroup) {
    if (isCollapsedComponentGroup(graphNode)) {
      this.graph.addLabel({
        owner,
        text: '',
        layoutParameter: urlNodeButtonModelParameter,
        style: urlButtonStyle,
        tag: graphNode,
      });
    } else {
      this.graph.addLabel({
        owner,
        text: '',
        layoutParameter: urlGroupNodeButtonModelParameter,
        style: WorkspaceModelOpenGroupUrlLabelStyle.Instance,
        tag: graphNode,
      });
    }
  }

  private setGroupNodeParents() {
    // Set parents of group nodes
    Array.from(this.groupNodeMap.values()).forEach(groupNode => {
      const parent =
        groupNode.tag instanceof GraphItem &&
        groupNode.tag.parent &&
        this.groupNodeMap.get(groupNode.tag.parent.id);

      if (parent) {
        this.graph.setParent(groupNode, parent);
      }
    });
  }
  private updateNodeLayout(node: INode) {
    const style = this.getNodeStyle(node.tag);
    const size = this.getNodeSize(style);
    const styleWidth = size?.width || getDefaultSize().width;
    const styleHeight = size?.height || getDefaultSize().height;
    const yAspectRatio = NODE_HEIGHT / styleHeight;
    const xAspectRatio = NODE_WIDTH / styleWidth;

    let width;
    let height;

    if (styleWidth * yAspectRatio > NODE_WIDTH) {
      width = NODE_WIDTH;
      height = styleHeight * xAspectRatio;
    } else {
      width = styleWidth * yAspectRatio;
      height = NODE_HEIGHT;
    }

    this.graph.setStyle(node, style);
    this.graph.setNodeLayout(
      node,
      new Rect(
        node.layout.topLeft,
        getNodeLayoutSize({
          styleWidth: width,
          styleHeight: height,
          hasContextHighlight: isContextNode(node),
          contextHighlightPadding: CONTEXT_HIGHLIGHT_PADDING,
          visualDiffType:
            node.tag instanceof GraphItem
              ? node.tag.getVisualDiffType()
              : DiffType.NONE,
        })
      )
    );
  }
  private addToScenarioButton(owner: INode, graphNode: GraphItem) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter: addToScenarioNodeButtonModelParameter,
      style: addToScenarioButtonStyle,
      tag: graphNode,
    });
  }
}
